import { useEffect, useState, type ReactNode, type TdHTMLAttributes } from "react";
import { Table } from "react-bootstrap";
import { sortBy } from "es-toolkit";

import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import Loader from "@/modules/shared/ui/Loader";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { classNames } from "@/lib/util";

type TableCellVM = TdHTMLAttributes<HTMLTableCellElement> & {
    value: string;
};
type TableRowVM = (string | TableCellVM)[];
type TableVM = { head: TableRowVM[]; body: TableRowVM[] };

const normalizeCell = (cell: string | TableCellVM): TableCellVM => {
    return typeof cell === "string" ? { value: cell } : cell;
};

const addLeftBorderToFirst = (row: TableRowVM, className = "text-right") => {
    return row.map((cell, index) => {
        if (index === 0) {
            return { value: normalizeCell(cell).value, className: classNames("cell--border-left", className) };
        }
        return cell;
    });
};

const getProductionIndexes = (record: t.IGetBillingStatisticsRecords): number[] =>
    sortBy(record.sum_types ?? [], ["month"]).map(({ month }) => month) ?? [];

const getPeopleIndexes = (record: t.IGetBillingStatisticsRecords): number[] =>
    sortBy(record.people ?? [], ["week"]).map(({ week }) => week) ?? [];

const getHeaderLabels = (numbers: number[]): string[] => numbers.map(num => (num === 0 ? "Current" : String(num)));

const getProductionValues = (record: t.IGetBillingStatisticsRecords, productionIndexes: number[]): string[] => {
    return productionIndexes.map(index => {
        const valuePerMonth = record.sum_types?.find(item => item.month === index);
        return valuePerMonth ? String(valuePerMonth.sum) : "-";
    });
};

const getPeopleValues = (record: t.IGetBillingStatisticsRecords, peopleIndexes: number[]): string[] => {
    return peopleIndexes.map(index => {
        const valuePerWeek = record.people?.find(item => item.week === index);
        return valuePerWeek ? String(valuePerWeek.count) : "-";
    });
};

const getTableVM = (data: t.IGetBillingStatisticsRecords[]): TableVM => {
    if (data.length === 0) {
        return {
            head: [],
            body: [],
        };
    }

    const productionIndexes = getProductionIndexes(data[0]);
    const peopleIndexes = getPeopleIndexes(data[0]);

    return {
        head: [
            [
                { value: "Type", rowSpan: 2, className: "text-left" },
                { value: "#", rowSpan: 2 },
                { value: "Open orders", rowSpan: 2 },
                {
                    value: "Production (by month)",
                    colSpan: productionIndexes.length,
                    className: "text-center cell--border-left",
                },
                {
                    value: "People (by week)",
                    colSpan: peopleIndexes.length,
                    className: "text-center cell--border-left",
                },
            ],
            [
                ...addLeftBorderToFirst(getHeaderLabels(productionIndexes)),
                ...addLeftBorderToFirst(getHeaderLabels(peopleIndexes)),
            ],
        ],
        body: data.map(record => {
            return [
                { value: record.type, className: "text-monospace" },
                String(record.count),
                String(record.open_orders),
                ...addLeftBorderToFirst(getProductionValues(record, productionIndexes)),
                ...addLeftBorderToFirst(getPeopleValues(record, peopleIndexes)),
            ];
        }),
    };
};

const renderContent = (table: TableVM): ReactNode => {
    if (table.body.length === 0) {
        return translate("common.no_data");
    }

    return (
        <Table className="dashboard-table mb-0">
            <thead className="table-header-neutral">
                {table.head.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => {
                            const normalizedCell = normalizeCell(cell);
                            return (
                                <th
                                    key={cellIndex}
                                    className={normalizedCell.className ?? "text-right"}
                                    rowSpan={normalizedCell.rowSpan}
                                    colSpan={normalizedCell.colSpan}
                                >
                                    {normalizedCell.value}
                                </th>
                            );
                        })}
                    </tr>
                ))}
            </thead>
            <tbody>
                {table.body.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => {
                            const normalizedCell = normalizeCell(cell);
                            return (
                                <td key={cellIndex} className={normalizedCell.className ?? "text-right"}>
                                    {normalizedCell.value}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

export default function BillingStats() {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [table, setTable] = useState<TableVM | null>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const res = await getBackend2().manufacturing.getBillingStatistics({});
                if (!cancelled) {
                    setTable(getTableVM(res.data));
                }
            } catch {
                if (!cancelled) {
                    setError(translate("common.error"));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section>
            <h2 className="content-subtitle">Billing Statistics</h2>
            <div className="card p-0 overflow-auto">
                <div className="card-content">
                    {error && (
                        <div className="m-3">
                            <ErrorComponent msg={error} type="error" />
                        </div>
                    )}
                    {loading && <Loader small={true} class_name="m-3"></Loader>}
                    {table && renderContent(table)}
                </div>
            </div>
        </section>
    );
}
